import React, { useState } from 'react';
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Anchor,
  Stack,
  Checkbox,
  Alert,
  LoadingOverlay,
  Center,
  Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconAlertCircle, IconLogin2, IconMail, IconLock } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import type { LoginCredentials } from '../../types/auth';

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  redirectTo?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onForgotPassword,
  redirectTo: _redirectTo = '/dashboard',
}) => {
  const { login, error, isLoading, clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const form = useForm<LoginCredentials>({
    initialValues: {
      email: '',
      password: '',
      remember_me: false,
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
      password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return null;
      },
    },
  });

  const handleSubmit = async (values: LoginCredentials) => {
    try {
      setIsSubmitting(true);
      clearError();

      const user = await login(values);

      // Call success callback or redirect
      if (onSuccess) {
        onSuccess();
      } else {
        // Get the intended destination from location state
        const from = (location.state as any)?.from;
        
        // If there's a specific destination, go there
        if (from) {
          navigate(from, { replace: true });
        } else {
          // Otherwise, redirect based on user role
          switch (user?.role) {
            case 'super_admin':
            case 'admin':
              navigate('/admin/dashboard', { replace: true });
              break;
            case 'teacher':
              navigate('/teacher/dashboard', { replace: true });
              break;
            default:
              navigate('/dashboard', { replace: true }); // Will redirect to appropriate dashboard
          }
        }
      }
    } catch (_error: any) {
      // Error is handled by auth context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    if (onForgotPassword) {
      onForgotPassword();
    } else {
      // Default behavior - could open a modal or navigate to reset page
      alert('Password reset functionality will be implemented');
    }
  };

  return (
    <Center style={{ minHeight: '100vh', backgroundColor: 'var(--mantine-color-gray-0)' }}>
      <Box style={{ width: '100%', maxWidth: 420 }}>
        <Paper
          radius="md"
          p="xl"
          withBorder
          style={{
            position: 'relative',
            backgroundColor: 'white',
            boxShadow: 'var(--mantine-shadow-lg)',
          }}
        >
          <LoadingOverlay visible={isLoading || isSubmitting} />

          {/* Header */}
          <Stack align="center" gap="sm" mb="xl">
            <Title
              order={2}
              style={{
                color: 'var(--mantine-color-admin-6)',
                fontWeight: 700,
              }}
            >
              YochienAI Admin
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Sign in to access the admin dashboard
            </Text>
          </Stack>

          {/* Error Alert */}
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              mb="md"
              onClose={clearError}
              withCloseButton
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Email"
                placeholder="your@email.com"
                size="sm"
                leftSection={<IconMail size={16} />}
                required
                {...form.getInputProps('email')}
                disabled={isSubmitting}
              />

              <PasswordInput
                label="Password"
                placeholder="Your password"
                size="sm"
                leftSection={<IconLock size={16} />}
                required
                {...form.getInputProps('password')}
                disabled={isSubmitting}
              />

              <Checkbox
                label="Remember me"
                size="sm"
                {...form.getInputProps('remember_me', { type: 'checkbox' })}
                disabled={isSubmitting}
              />

              <Button
                type="submit"
                fullWidth
                size="sm"
                leftSection={<IconLogin2 size={16} />}
                loading={isSubmitting}
                disabled={isLoading}
                style={{
                  backgroundColor: 'var(--mantine-color-admin-6)',
                  '&:hover': {
                    backgroundColor: 'var(--mantine-color-admin-7)',
                  },
                }}
              >
                Sign In
              </Button>
            </Stack>
          </form>

          {/* Footer Links */}
          <Stack gap="xs" mt="xl" align="center">
            <Anchor
              component="button"
              type="button"
              size="sm"
              onClick={handleForgotPassword}
              disabled={isSubmitting}
            >
              Forgot your password?
            </Anchor>
            
            <Text size="xs" c="dimmed" ta="center">
              Need help? Contact your system administrator
            </Text>
          </Stack>
        </Paper>
      </Box>
    </Center>
  );
};

export default LoginForm;